import {cloneDeep} from "lodash";
import { useRoute, useRouter } from "vue-router";
import { useDefaultStore } from "../store/crm.store";

/**
 * Questa composition api si occupa di gestire i push/delete nell'url
 * e di eseguire le funzionalità inserite nell'url dopo un eventuale refresh pagina
 */
export function useFeacturesRouter() {

  const route = useRoute()
  const router = useRouter()
  const {feacturesList} = useDefaultStore()

  /**
   * parametri di default da non cancellare mai dall'url
   * !pratica_id possiede un eccezzione. Essa è importante solo per la pagina Pratica.
   * Nelle altre pagine se c'è una funzionalità che pusha il pratica_id, essa andrebbe rimossa dalla delete
   * @type {string[]}
   */
  const defaultParams = ['pratica_id', 'azioni']

  /**
   * Se siamo in una pagina diversa da Pratica, toglie pratica_id dalle variabili non cancellabili via URL
   * @return {string|string[]}
   */
  const checkPageParams = () => (route.name !== "pratica") ? defaultParams.slice(1) : defaultParams;

  const locationPath = (window.location.pathname).replace('/','')

  /**
   * Registra per ogni pagina l'elenco delle funzionalità da attivare via link
   * @param urlMapKey
   * @param callback
   * @param params
   * @param mode
   * @return {{fn, property}}
   */
  const registerFeacture = (urlMapKey, callback, params, mode = null) => {
    let obj = {};
    obj[urlMapKey] = { fn: callback, property: params, mode: mode };
    feacturesList.value = {...feacturesList.value, ...obj};
  }

  /**
   * Aggiorna una funzionalità dall'elenco delle funzionalità da attivare via link
   * @param id
   * @param params
   */
  const updateFeacture = (id, params) => feacturesList.value[id].property = params;

  /**
   * Legge i parametri nell'url ed esegue le azioni necessarie
   */
  function runFeactures() {
    setTimeout(() => {
      if(_haveAzioni()) {
        let actions = (cloneDeep(route.query.azioni)).split('|')
        actions.forEach((action) => {
          if(feacturesList.value[action]){
            dispatch(
              feacturesList.value[action].fn,
              feacturesList.value[action].property ? _getActionParamsByUrl(feacturesList.value[action].property, feacturesList.value[action].mode) : null
            )
          }
        });
      }
    }, 200)
  }

  /**
   * Aggiunge elemento alle azioni
   * @param {String} id
   * @param params
   * @private
   */
  async function _updateActions(id, params) {
    let azioniDaFillare = route && route.query['azioni'] !== undefined ? cloneDeep(route.query.azioni) + '|' + id : id;
    let copiaQueres = cloneDeep(route.query)
    copiaQueres['azioni'] = azioniDaFillare

    if(params) {
      Object.keys(params).forEach((paramKey) => {
        copiaQueres[paramKey] = params[paramKey]
      })
    }

    await _pushAzioniInUrl(copiaQueres)
  }

  /**
   * Pusha azione in url se non già presente
   * @param id
   * @param params
   */
  async function pushAction(id, params) {
      let pushInURL = true;
      if(_haveAzioni()) {
        // se true indica che l'id non è già presente nell'url
        pushInURL = !(_getAzioni()).includes(id);
      }

      if(pushInURL) await _updateActions(id, params);
  }

  /**
   * Pusha azioni e nuovo url generato
   * @param query
   * @private
   */
  async function _pushAzioniInUrl(query) {
    let routerParams = {
      name: locationPath,
      query: query
    }
    if(routerParams.query.azioni === '') delete routerParams.query.azioni
    await router.push(routerParams)
  }

  /**
   * Rimuove dall'url l'azione specificata e tutti i parametri associati
   * @param {String} id
   * @param tab
   */
  async function deleteAction(id, tab = null) {
    if(!_haveAzioni()) return;

    let actions = _getAzioni();
    let indexDiPartenzaDaCancellare = null;

    if(!tab || (tab && !actions.includes(tab))) {
      actions.forEach((azione, index) => {
        if(azione === id) {
          actions.splice(index, 1);
          indexDiPartenzaDaCancellare = index;
        }

        if(index >= indexDiPartenzaDaCancellare && indexDiPartenzaDaCancellare != null) actions.splice(index, 1);
      })
    }

    let copiaQueres = cloneDeep(route.query)

    // In base al mapping dei metodi capisce quali parametri togliere dall'url
    // per funzionare necessita che la chiave del mapping corrisponda alla stringa usata per pushare la modale nell'url
    if(!tab || (tab && !actions.includes(tab))) {
      if (feacturesList.value[id] && feacturesList.value[id].property) {
        Object.keys(copiaQueres).forEach((index) => {
          if (_canDelete(feacturesList.value[id].property, index) && !checkPageParams().includes(index)) {
            delete copiaQueres[index];
          }
        });
      }
    }

    copiaQueres['azioni'] = actions.join('|')

    await _pushAzioniInUrl(copiaQueres)
  }

  /**
   * Ritorna true se trova la chiave di ricerca tra le proprietà passate
   * @param property
   * @param search
   * @private
   */
  function _canDelete(property, search) {
    let result = false;
    if(Array.isArray(property)) result = (property).includes(search)
    if(!(Array.isArray(property))) result = Object.values(property).indexOf(search) > -1

    return result;
  }

  /**
   * Restituisce lo split delle azioni in url
   * @return {*|*[]}
   */
  const _getAzioni = () => _haveAzioni() ? cloneDeep(route.query.azioni.split('|')) : []

  /**
   * Recupera valore dei parametri dall'url in base a quelli che servono alla funzione
   * @param params
   * @param mode
   */
  function _getActionParamsByUrl(params, mode) {
    let paramsToSend = [];
    let paramToExe = {};
    let routeQueryClone = cloneDeep(route.query)
    // parametri presi dall'url
    if(Array.isArray(params)) {
      params.forEach((param) => {
        if(routeQueryClone[param]) {
          paramToExe[param] = routeQueryClone[param]
          paramsToSend.push(routeQueryClone[param]);
        }
      })
    }

    // parametri passati via registrazione feacture
    if(!(Array.isArray(params))) {
      Object.values(params).forEach((param) => {
        if(routeQueryClone["azioni"].includes(param)) paramsToSend.push(param)
      })
    }

    return mode === 'modal' ? [paramToExe] : paramsToSend;
  }

  /**
   * Esegue la funzione passata, con i parametri passati
   * @param fn
   * @param args
   * @return {*}
   */
  function dispatch(fn, args) {
    fn = (typeof fn == "function") ? fn : window[fn];
    return fn.apply(this, args || []);
  }

  /**
   * Se true indica che vi sono azioni nell'url
   * @return {boolean}
   */
  const _haveAzioni = () => route && route['query'] !== undefined && route.query['azioni'] !== undefined

  /**
   * controlla se esiste un azione specifica
   * ex. nelle tabs usata per capire quale tab togliere dall'url dopo un suo toggle
   * @param azione
   * @return {false|*|boolean}
   */
  const haveAzione = (azione) => _haveAzioni() && route.query['azioni'].includes(azione)

  return {
    runFeactures,
    pushAction,
    deleteAction,
    registerFeacture,
    haveAzione,
    updateFeacture
  }
}